import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Save, Sparkles } from 'lucide-react';
import { Skeleton, useToast } from '@qh/shared';
import { ApiError, candidateApi, type CandidateProfile, type MasterDataRef } from '../../lib/apiClient';
import {
  ProfileFormSections,
  profileFieldId,
  validateProfileForm,
} from '../../components/profile/ProfileFormSections';
import {
  buildUpdateBody,
  emptyForm,
  formFromProfile,
  type ProfileFormState,
} from '../../components/profile/profileFormShape';
import { ReParseModal } from '../../components/profile/ReParseModal';
import { useAuth } from '../../context/AuthContext';
import {
  profileFieldDomId,
  profileFieldErrorsFromApiError,
  type ProfileFieldErrors,
  type ProfileFieldKey,
} from '../../lib/profileValidation';

/**
 * My Profile page (analysis §5.3) — full editable form for the candidate's
 * profile. Loads via GET `/candidates/me/profile`, edits in local state, saves
 * via PATCH `/candidates/me/profile`. Re-parse from an existing resume opens
 * a small picker that overwrites work_history / education from a fresh AI parse.
 *
 * Mount-time fetch follows the canonical StrictMode-safe pattern (CLAUDE.md
 * Frontend Conventions): cleanup-cancellation flag + ref pinned AFTER the fetch
 * resolves so dev double-mount doesn't strand the page in an empty state.
 */
export function MyProfilePage() {
  const toast = useToast();
  const { refresh: refreshAuth } = useAuth();
  const [profile, setProfile] = useState<CandidateProfile | null>(null);
  const [form, setForm] = useState<ProfileFormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reParseOpen, setReParseOpen] = useState(false);
  /**
   * Skill rows that the SkillPicker chip renderer needs to resolve names for ids
   * that fall outside its bootstrap top-50 catalog. Seeded from two sources:
   *   1. `profile.skills` on initial load + after every save (already returned by
   *      `GET /candidates/me/profile` as `{id, slug, name}`). Without this, chips
   *      render UUIDs after a navigate-away/come-back because `formFromProfile`
   *      keeps only ids in form state.
   *   2. `/public/skills?ids=...` after a re-parse, for matched_skill_ids that
   *      aren't in the freshly-loaded profile.
   * Merged dedup-by-id so the two sources compose.
   */
  const [skillCatalog, setSkillCatalog] = useState<MasterDataRef[]>([]);
  const mergeIntoSkillCatalog = useCallback((rows: MasterDataRef[]) => {
    if (rows.length === 0) return;
    setSkillCatalog((prev) => {
      const seen = new Set(prev.map((s) => s.id));
      const additions = rows.filter((s) => !seen.has(s.id));
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, []);
  /** Backend field-level validation errors keyed by form field. */
  const [fieldErrors, setFieldErrors] = useState<ProfileFieldErrors>({});
  const fetchedOnceRef = useRef(false);

  const load = useCallback(async () => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    try {
      const p = await candidateApi.profile.get();
      if (cancelled) return;
      setProfile(p);
      setForm(formFromProfile(p));
      mergeIntoSkillCatalog(p.skills ?? []);
    } catch (err) {
      if (cancelled) return;
      setError(err instanceof ApiError ? err.message : 'Could not load your profile.');
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (fetchedOnceRef.current) return;
    let cancelled = false;
    setLoading(true);
    candidateApi.profile
      .get()
      .then((p) => {
        if (cancelled) return;
        fetchedOnceRef.current = true;
        setProfile(p);
        setForm(formFromProfile(p));
        mergeIntoSkillCatalog(p.skills ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load your profile.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const showFieldErrors = useCallback(
    (errors: ProfileFieldErrors) => {
      setFieldErrors(errors);
      toast.error('Please fix the highlighted fields.');
      const firstKey = Object.keys(errors)[0] as ProfileFieldKey | undefined;
      if (!firstKey) return;
      requestAnimationFrame(() => {
        const el = document.getElementById(profileFieldDomId(firstKey));
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
          ?.focus({ preventScroll: true });
      });
    },
    [toast],
  );

  const clearFieldError = useCallback((key: ProfileFieldKey) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    const invalid = validateProfileForm(form);
    if (invalid) {
      setSaveError(invalid.message);
      const el = document.getElementById(profileFieldId(invalid.field));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
      }
      return;
    }
    setSaving(true);
    setSaveError(null);
    setFieldErrors({});
    try {
      const updated = await candidateApi.profile.update(buildUpdateBody(form));
      setProfile(updated);
      setForm(formFromProfile(updated));
      mergeIntoSkillCatalog(updated.skills ?? []);
      toast.success('Profile saved.');
      // If this save flipped profile_completed=TRUE, the auth bootstrap mirrors that
      // back to AuthContext so the banner / gate UI updates.
      void refreshAuth();
    } catch (err) {
      // Try field-level errors first; fall back to top-level banner.
      const fieldErrs = profileFieldErrorsFromApiError(err);
      if (Object.keys(fieldErrs).length > 0) {
        showFieldErrors(fieldErrs);
      } else {
        setSaveError(err instanceof ApiError ? err.message : 'Could not save your profile.');
      }
    } finally {
      setSaving(false);
    }
  }, [form, refreshAuth, toast, showFieldErrors]);

  const handleReParseDone = useCallback(
    (next: ProfileFormState, hydratedSkills: MasterDataRef[]) => {
      setForm(next);
      mergeIntoSkillCatalog(hydratedSkills);
      setReParseOpen(false);
      toast.success('Profile pre-filled from your resume. Review and save when ready.');
    },
    [toast, mergeIntoSkillCatalog],
  );

  if (loading) {
    return <ProfileSkeleton />;
  }

  if (error || !profile) {
    return (
      <div className="bg-white rounded-3xl border border-red-100 shadow-sm p-6 text-center">
        <p className="text-sm font-bold text-red-700 mb-3">{error ?? 'Profile unavailable.'}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="text-xs font-black uppercase tracking-[0.18em] text-brand-primary hover:text-brand-deep"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <section>
      <header className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-brand-primary">
            My Profile
          </h1>
          <p className="text-sm text-brand-muted mt-1">
            Update your details so companies can find and assess you accurately.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setReParseOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-2xl border border-brand-accent/40 bg-brand-secondary/40 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-brand-deep hover:bg-brand-secondary transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5 fill-brand-accent text-brand-accent" />
          Re-parse from resume
        </button>
      </header>

      <ProfileFormSections
        form={form}
        setForm={setForm}
        email={profile.email}
        regionName={profile.region.name}
        initialSkillCatalog={skillCatalog}
        fieldErrors={fieldErrors}
        onFieldChange={clearFieldError}
        rtrConsentAt={profile.rtr_consent_at}
        fraudConsentAt={profile.fraud_declaration_consent_at}
      />

      <div className="sticky bottom-4 mt-6 z-10">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-4 flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            {saveError && <p className="text-sm text-red-700 font-medium truncate">{saveError}</p>}
            {!saveError && (
              <p className="text-xs text-brand-muted truncate">
                Changes are saved when you click Save.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-2xl bg-brand-primary px-5 py-2.5 text-xs font-black uppercase tracking-[0.18em] text-white hover:bg-brand-deep disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
          >
            {saving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                Save changes
              </>
            )}
          </button>
        </div>
      </div>

      <ReParseModal
        open={reParseOpen}
        onClose={() => setReParseOpen(false)}
        onParsed={handleReParseDone}
        currentForm={form}
      />
    </section>
  );
}

function ProfileSkeleton() {
  return (
    <section className="space-y-5">
      <div>
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-72" />
      </div>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 space-y-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
    </section>
  );
}
